import { useCallback, useMemo } from 'react'
import { createContainer } from 'unstated-next'

import {
  availablePlacementsForType,
  canAddType,
  getAvailabilityMatrix,
  validateSelectionSet,
} from '../combination-rules/engine'
import { EmbroideryTypeKey, PlacementRule } from '../combination-rules/types'

import { useEmbroideryGarment } from './EmbroideryGarmentProvider'
import { useEmbroiderySelection } from './EmbroiderySelectionProvider'

/**
 * What the customer can still do: which types of embroidery can be added, which placements are open
 * to each, where an item they are editing could move to, and what would stop them submitting.
 */
function useEmbroideryAvailabilityImpl() {
  const { constraints } = useEmbroideryGarment()
  const { items: embroideryItems } = useEmbroiderySelection()

  const matrix = useMemo(
    () => getAvailabilityMatrix(embroideryItems, constraints),
    [embroideryItems, constraints]
  )

  const editingMatrixCache = useMemo(
    () => new Map<string, ReturnType<typeof getAvailabilityMatrix>>(),

    /** We want to invalidate the cache when EmbroieryItems and constraints update and
     * that goes against the eslint rule since they aren't directly used in the memo */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [embroideryItems, constraints]
  )

  /**
   * The matrix as it would be with one item set aside, for a picker that is *moving* that item
   * rather than adding another.
   *
   * A type the garment takes only once — a logo, an icon — blocks every placement as soon as one
   * is chosen, including the one it is sitting on. A picker showing that item would then have
   * nothing selectable in it. Leaving the item out answers the question the picker is actually
   * asking: where could this go instead?
   */

  const matrixForEditing = useCallback(
    (instanceId: string) => {
      const cached = editingMatrixCache.get(instanceId)
      if (cached !== undefined) return cached

      const result = getAvailabilityMatrix(
        embroideryItems.filter(item => item.instanceId !== instanceId),
        constraints
      )
      editingMatrixCache.set(instanceId, result)
      return result
    },
    [editingMatrixCache, embroideryItems, constraints]
  )

  const canAdd = useCallback(
    (typeKey: EmbroideryTypeKey) => canAddType(typeKey, embroideryItems, constraints),
    [embroideryItems, constraints]
  )

  const placementsForType = useCallback(
    (typeKey: EmbroideryTypeKey) =>
      availablePlacementsForType(typeKey, embroideryItems, constraints),
    [embroideryItems, constraints]
  )

  /**
   * Where an item the customer is already editing could move to. It asks with that item left out,
   * so the placement it currently occupies still counts as open to it.
   *
   * `undefined` means there is no such item, which is a stale id rather than an answer. An empty
   * list is the answer that the item has nowhere to go, and the two want different handling — a
   * picker with nothing in it against a panel open on something that is no longer selected.
   */
  const placementsForItem = useCallback(
    (instanceId: string): readonly PlacementRule[] | undefined => {
      const target = embroideryItems.find(item => item.instanceId === instanceId)
      if (!target) return undefined

      return availablePlacementsForType(
        target.typeKey,
        embroideryItems.filter(item => item.instanceId !== instanceId),
        constraints
      )
    },
    [embroideryItems, constraints]
  )

  /**
   * The backstop for the submit button. Normally empty, because nothing the UI offers can break a
   * rule — a selection carried over from another garment, or a required type never filled in, is
   * what shows up here.
   */
  const selectionErrors = useMemo(
    () => validateSelectionSet(embroideryItems, constraints),
    [embroideryItems, constraints]
  )

  return { matrix, matrixForEditing, canAdd, placementsForType, placementsForItem, selectionErrors }
}

const EmbroideryAvailabilityContainer = createContainer(useEmbroideryAvailabilityImpl)

export const EmbroideryAvailabilityProvider = EmbroideryAvailabilityContainer.Provider
export const useEmbroideryAvailability = EmbroideryAvailabilityContainer.useContainer
